<template>
  <md-card class="exchange-portfolio">
    <md-card-header class="exchange-portfolio__header">
      <div class="md-title exchange-portfolio__title">{{ i18n.dash_wallet() }}</div>

      <div v-if="currentAsset.length" class="exchange-portfolio__select-wrp">
        <select-field :label="i18n.lbl_exchange_sell()"
                      :value="currentAsset"
                      :values="tokens"
                      @input="$emit(events.assetChange, $event)"
        />
      </div>
    </md-card-header>

    <template v-if="noBalances">
      <div class="exchange-portfolio__no-data">
        <md-icon class="md-size-4x">autorenew</md-icon>
        <h3>{{ i18n.th_no_balances_in_your_wallet() }}</h3>
        <p>{{ i18n.th_here_will_be_tokens() }}</p>
      </div>
    </template>

    <template v-else-if="currentAsset.length">
      <md-card-content class="exchange-portfolio__amount">
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-small-size-100">
            <input-field id="transfer-amount"
                         name="amount"
                         type="number"
                         v-model="toConvert"
                         :data-vv-as="i18n.lbl_give()"
                         :label="i18n.lbl_give()"
                         vvValidateOn="change"
                         v-validate="'amount'"
                         :step="0.01"
                         :errorMessage="errors.first('amount') ||
                            (checkTransferable ? i18n.tx_greater_than_balance() : '')"
                         @blur="checkAmount"
            />
          </div>

          <div class="md-layout-item md-small-size-100">
            <input-field id="convert-amount"
            :label="i18n.lbl_get()"
                         name="converted"
                         type="text"
                         :value="`${i18n.c(convertedAmount)} ${convertAsset}`"
                         :readonly="true"
            />
          </div>
        </div>
      </md-card-content>

      <md-card-content class="exchange-portfolio__asset">
        <div class="md-alignment-bottom-center">
          <div class="exchange-portfolio__asset-picture">
            <img class="exchange-portfolio__asset" :src="imgUrl">
          </div>
          <div class="exchange-portfolio__asset-available">
            <div class="exchange-portfolio__asset-value">{{ balance }} {{ currentAsset }}</div>
            <div v-if="currentAsset !== 'USD'" class="exchange-portfolio__asset-usd">{{ convertedBalance }} USD</div>
          </div>
        </div>
      </md-card-content>

      <md-card-actions class="exchange-portfolio__actions">
        <md-button @click="onExchange"
                   class="md-primary btn btn_narrow md-yellow w-100"
                   :disabled="checkTransferable || !convertedAmount"
        >{{ i18n.lbl_exchange() }}</md-button>
      </md-card-actions>
    </template>

    <template v-else-if="loaded">
      <md-card-content>
        <div class="exchange-portfolio__no-data">
          <md-icon class="md-size-4x">autorenew</md-icon>
          <h2>{{ i18n.th_no_assets_in_your_wallet() }}</h2>
          <p>{{ i18n.th_here_will_be_tokens() }}</p>
        </div>
      </md-card-content>
    </template>
    <template v-else>
      <div class="exchange-portfolio__no-data">
        <h3>{{ i18n.lbl_loading() }}</h3>
      </div>
    </template>

  </md-card>
</template>

<script>
  import config from '@conf'
  import FormMixin from '@com/mixins/form.mixin'

  import SelectField from '@com/fields/SelectField'
  import ConfirmTransfer from '@vue/app/transfers/make/Transfers.Confirm'

  import { mapGetters, mapActions } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { i18n } from '@js/i18n'
  import { commonEvents } from '@js/events/common_events'
  import debounce from 'lodash/debounce'
  import get from 'lodash/get'
  import { pairsService } from '@api/pairs.service'
  import { ErrorHandler } from '@js/errors/error_handler'
  import CurrencyRound from '@com/mixins/currency.round.mixin'
  import {isCryptoAsset} from '@utils/consts'

  export default {
    name: 'exchange-portfolio',
    mixins: [FormMixin, CurrencyRound],
    components: { SelectField, ConfirmTransfer },

    data: _ => ({
      i18n,
      toConvert: '',
      events: {
        assetChange: commonEvents.assetChangeEvent
      }
    }),
    computed: {
      ...mapGetters([
        vuexTypes.accountBalances,
        vuexTypes.userCashTokens,
        vuexTypes.statsQuoteToken,
        vuexTypes.operationalAccountId
      ]),
      noBalances () {
        let nobalances = this.loaded
        this.tokens.forEach(code => {
          nobalances &= Number(get(this.accountBalances, `${code}.balance`)) === 0
        })
        return this.loaded ? nobalances : false
      },
      tokens () {
        if (this.userCashTokens.length) {
          return this.userCashTokens.map(token => token.code)
        }
        return []
      },
      balance () {
        if (isCryptoAsset(this.currentAsset)) {
          let balanceToRound = this.roundCurrency2(get(this.accountBalances, `${this.currentAsset}.balance`), this.currentAsset)
          return i18n.c(balanceToRound || 0)
        } else {
          let balanceToRound = this.roundCurrency(get(this.accountBalances, `${this.currentAsset}.balance`), this.currentAsset)
          return i18n.c(balanceToRound || 0)
        }
      },
      convertedBalance () {
        return i18n.cc(get(this.accountBalances, `${this.currentAsset}.converted_balance`) || 0)
      },
      imgUrl () {
        const logoKey = get(this.accountBalances, `${this.currentAsset}.asset_details.details.logo.key`)
        if (logoKey) {
          return `${config.FILE_STORAGE}/${logoKey}`
        }
        const defaultUrl = '../../../../../static/coin-picture.png'
        return defaultUrl
      },
      checkTransferable () {
        return this.enough()
      },
      loaded () {
        return this.$parent.loadComplete
      }
    },

    props: ['currentAsset', 'convertAsset', 'convertedAmount'],

    methods: {
      ...mapActions({
        loadBalances: vuexTypes.GET_ACCOUNT_BALANCES
      }),
      async created () {
        await this.loadBalances()
      },
      enough () {
        let bal = Number(get(this.accountBalances, `${this.currentAsset}.balance`))
        return bal < this.toConvert
      },
      checkAmount () {
        if (isCryptoAsset(this.currentAsset)) {
          this.toConvert = this.roundCurrency2(this.toConvert, this.currentAsset)
        } else {
          this.toConvert = this.roundCurrency(this.toConvert, this.currentAsset)
        }
      },
      async onExchange () {
        const from = {asset: this.currentAsset.toString(), amount: parseFloat(this.toConvert)}
        const to = {asset: this.convertAsset.toString(), amount: parseFloat(this.convertedAmount)}

        try {
          let order = await pairsService.getOrder(from, to)
          this.$router.push({
            name: 'transfers.make:tokenCode',
            params: {
              tokenCode: this.currentAsset,
              amount: this.toConvert,
              recipient: this.operationalAccountId,
              subject: order.id,
              convert: true }
          })
        } catch (error) {
          console.log('ERROR', error)
          ErrorHandler.processUnexpected(error)
        }
      },
      async reset () {
        this.$parent.$emit('convert-amount', {toConvert: 0})
      }
    },
    watch: {
      currentAsset: debounce(function () { this.reset() }, 0),
      toConvert: function (value) {
        this.$parent.$emit('convert-amount', {toConvert: Number.parseFloat(value)})
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '~scss/variables.scss';
  @import '~scss/mixins.scss';

  $custom-breakpoint: 860px;

  .w-100 {
    width: 100%;
  }

  .exchange-portfolio {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 470px;
    min-width: 248px;

    @include respond-to(medium) {
      width: 100%;
      max-width: inherit;
      padding: 0;
    }

    @include respond-to(medium) {
      margin-bottom: 110px;
    }

    @include respond-to(small) {
      padding: 8px;
    }

    &__no-data {
      padding: 16px;
      text-align: center;
    }
  }

  .exchange-portfolio__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    @include respond-to(medium) {
      padding: 24px 24px 0 24px!important;
    }
  }

  .exchange-portfolio__title {
    @include respond-to(medium) {
      font-size: 20px;
    }

    @include respond-to(xsmall) {
      font-size: 14px;
    }
  }

  .exchange-portfolio__select-wrp {
    max-width: 160px;
  }

  .exchange-portfolio__asset {
    flex-grow: 1;
    text-align: center;

    @include respond-to(medium) {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0;
      padding: 0 24px 24px 24px!important;
    }
  }

  .exchange-portfolio__amount {
    text-align: center;

    @include respond-to(medium) {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0;
      padding: 0 24px 24px 24px!important;
    }
  }

  .exchange-portfolio__asset-picture {
    margin-bottom: 32px;

    @include respond-to(medium) {
      margin-right: 16px;
      margin-bottom: 0;
    }

    img {
      width: 50px;
      height: 50px;

      @include respond-to(medium) {
        width: 50px;
        height: 50px;
        padding: 0;
      }
    }

    .exchange-portfolio__asset {
      @include respond-to(medium) {
        padding: 0 !important;
      }
    }
  }

  .exchange-portfolio__asset-value {
    margin-bottom: 16px;
    font-size: 24px;
    letter-spacing: .2px;

    @include respond-to(medium) {
      font-size: 20px;
      margin-bottom: 4px;
    }

    @include respond-to(xsmall) {
      font-size: 16px;
    }
  }

  .exchange-portfolio__asset-usd {
    font-size: 16px;
    letter-spacing: .2px;
    color: rgba(0, 0, 0, .55);

    @include respond-to(xsmall) {
      font-size: 14px;
    }
  }

  .exchange-portfolio__actions {
    @include respond-to(medium) {
      padding: 8px;
      margin-left: auto;
    }
  }
  /deep/ .md-field .md-error {
    overflow: unset;
  }

</style>



// WEBPACK FOOTER //
// src/vue/app/exchange/index/Exchange.Portfolio.vue